/**
 * Arabic Kart Luxe Theme Engine v3.0
 * Features:
 * 1. Dark Mode & Light Mode switcher with persistence
 * 2. English & Arabic (RTL) language switcher with persistence
 * 3. 40-Minute Sale Timer (Counts down; resets upon refresh once expired)
 * 4. Modern Search Icon modal overlay with instant live tags
 * 5. Direct 1-Click Cash on Delivery (COD) order modal & instant direct checkout
 * 6. Shopping Cart dynamic controls (+, -, manual, remove, subtotal, empty state)
 * 7. Product Gallery with 2.1x desktop hover zoom, lightbox, swipe, and arrows
 * 8. Moving Product Banner carousel
 */

document.addEventListener('DOMContentLoaded', function() {
  initThemeAndLanguage();
  init40MinSaleTimer();
  initModernSearch();
  initBuyNowAndCod();
  initCartDrawer();
  initProductGallery();
  initMovingBanner();
  initQuantityControls();
  initReviewForm();
});

// ==========================================
// 1. THEME MODE (DARK / LIGHT) & LANGUAGE (EN / AR)
// ==========================================
function initThemeAndLanguage() {
  // Theme Mode
  const savedTheme = localStorage.getItem('arabic_kart_theme') || 'dark';
  document.documentElement.setAttribute('data-theme', savedTheme);
  updateThemeIcon(savedTheme);

  document.querySelectorAll('[data-toggle-theme]').forEach(btn => {
    btn.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('data-theme') || 'dark';
      const next = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('arabic_kart_theme', next);
      updateThemeIcon(next);
    });
  });

  function updateThemeIcon(mode) {
    document.querySelectorAll('[data-theme-icon]').forEach(icon => {
      icon.textContent = mode === 'dark' ? '☀️' : '🌙';
    });
  }

  // Language Switcher
  const savedLang = localStorage.getItem('arabic_kart_lang') || 'en';
  applyLanguage(savedLang);

  document.querySelectorAll('[data-toggle-lang]').forEach(btn => {
    btn.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('lang') || 'en';
      const next = current === 'en' ? 'ar' : 'en';
      applyLanguage(next);
      localStorage.setItem('arabic_kart_lang', next);
    });
  });

  function applyLanguage(lang) {
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    document.querySelectorAll('[data-lang-label]').forEach(el => {
      el.textContent = lang === 'ar' ? 'English' : 'العربية';
    });
  }
}

// ==========================================
// 2. 40-MINUTE SALE TIMER (RESETS ON REFRESH AFTER FINISH)
// ==========================================
function init40MinSaleTimer() {
  const timerElements = document.querySelectorAll('[data-ak-countdown-40]');
  if (!timerElements.length) return;

  const STORAGE_KEY = 'arabic_kart_sale_timer_deadline_v3';
  const DURATION_MS = 40 * 60 * 1000;

  let deadline = localStorage.getItem(STORAGE_KEY);
  const now = Date.now();

  // If expired or not set, RESET to 40:00!
  if (!deadline || isNaN(deadline) || (parseInt(deadline, 10) - now <= 0)) {
    deadline = now + DURATION_MS;
    localStorage.setItem(STORAGE_KEY, deadline);
  } else {
    deadline = parseInt(deadline, 10);
  }

  function update() {
    const current = Date.now();
    const remaining = deadline - current;

    if (remaining <= 0) {
      timerElements.forEach(el => {
        el.innerHTML = '<span style="color:#F59E0B;font-weight:900;">00:00</span>';
      });
      return;
    }

    const minutes = Math.floor(remaining / (1000 * 60));
    const seconds = Math.floor((remaining / 1000) % 60);

    const pad = n => String(n).padStart(2, '0');
    const timeStr = pad(minutes) + ':' + pad(seconds);

    timerElements.forEach(el => {
      el.textContent = timeStr;
    });
  }

  update();
  setInterval(update, 1000);
}

// ==========================================
// 3. MODERN LUXURY SEARCH OVERLAY
// ==========================================
function initModernSearch() {
  const modal = document.querySelector('[data-search-modal]');
  const triggers = document.querySelectorAll('[data-search-trigger]');
  const closes = document.querySelectorAll('[data-search-close]');
  const input = modal ? modal.querySelector('input[type="search"]') : null;

  function openSearch() {
    if (modal) {
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      if (input) setTimeout(() => input.focus(), 100);
    }
  }

  function closeSearch() {
    if (modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  triggers.forEach(t => t.addEventListener('click', openSearch));
  closes.forEach(c => c.addEventListener('click', closeSearch));

  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeSearch();
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
      closeSearch();
    }
  });

  // Search pills auto-fill
  document.querySelectorAll('[data-search-tag]').forEach(pill => {
    pill.addEventListener('click', () => {
      const term = pill.getAttribute('data-search-tag');
      window.location.href = '/search?q=' + encodeURIComponent(term);
    });
  });
}

// ==========================================
// 4. CASH ON DELIVERY (COD) MODAL & BUY NOW
// ==========================================
function initBuyNowAndCod() {
  // Variant Selection Listener
  document.addEventListener('change', function(e) {
    const sel = e.target.closest('[data-variant-select]');
    if (!sel) return;
    const opt = sel.options[sel.selectedIndex];
    if (!opt) return;

    const price = opt.getAttribute('data-price');
    const compare = opt.getAttribute('data-compare');

    const priceEl = document.querySelector('[data-product-price]');
    if (priceEl && price) priceEl.textContent = price;

    const compareEl = document.querySelector('[data-compare-price]');
    if (compareEl) {
      if (compare) {
        compareEl.textContent = compare;
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }

    const buyNowBtn = document.querySelector('[data-buy-now]');
    if (buyNowBtn) buyNowBtn.setAttribute('data-variant-id', opt.value);
    const codBtn = document.querySelector('[data-open-cod]');
    if (codBtn) codBtn.setAttribute('data-variant-id', opt.value);
  });

  // Direct Buy Now -> Direct to /checkout
  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-buy-now]');
    if (!btn) return;
    e.preventDefault();

    let variantId = btn.getAttribute('data-variant-id');
    const form = btn.closest('form');
    if (form) {
      const select = form.querySelector('select[name="id"]');
      const hiddenInput = form.querySelector('input[name="id"]');
      if (select && select.value) variantId = select.value;
      else if (hiddenInput && hiddenInput.value) variantId = hiddenInput.value;
    }

    if (!variantId) {
      alert('Please select a product option.');
      return;
    }

    let qty = 1;
    if (form) {
      const qtyInput = form.querySelector('[data-product-qty]') || form.querySelector('input[name="quantity"]');
      if (qtyInput) qty = parseInt(qtyInput.value, 10) || 1;
    }

    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span>⚡ Securing Order...</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] })
    })
    .then(r => {
      if (!r.ok) throw new Error('Out of stock or unavailable');
      return r.json();
    })
    .then(() => {
      window.location.href = '/checkout';
    })
    .catch(err => {
      alert('Checkout error: ' + err.message);
      btn.disabled = false;
      btn.innerHTML = originalHtml;
    });
  });

  // COD Quick Order Modal Handlers
  const codModal = document.querySelector('[data-cod-modal]');
  const codClose = document.querySelectorAll('[data-cod-close]');
  const codForm = document.querySelector('[data-cod-form]');

  function openCodModal(variantId, title, price) {
    if (!codModal) return;
    const vInput = codModal.querySelector('[name="cod_variant_id"]');
    const pTitle = codModal.querySelector('[data-cod-product-title]');
    const pPrice = codModal.querySelector('[data-cod-product-price]');
    if (vInput && variantId) vInput.value = variantId;
    if (pTitle && title) pTitle.textContent = title;
    if (pPrice && price) pPrice.textContent = price;
    codModal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  function closeCodModal() {
    if (codModal) {
      codModal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  document.addEventListener('click', function(e) {
    const codTrigger = e.target.closest('[data-open-cod]');
    if (!codTrigger) return;
    e.preventDefault();
    const vid = codTrigger.getAttribute('data-variant-id') || '';
    const title = codTrigger.getAttribute('data-product-title') || 'Arabic Kart Product';
    const price = codTrigger.getAttribute('data-product-price') || '';
    openCodModal(vid, title, price);
  });

  codClose.forEach(c => c.addEventListener('click', closeCodModal));
  if (codModal) {
    codModal.addEventListener('click', (e) => {
      if (e.target === codModal) closeCodModal();
    });
  }

  // Handle COD Form Submission
  if (codForm) {
    codForm.addEventListener('submit', function(e) {
      e.preventDefault();
      const name = codForm.querySelector('[name="customer_name"]').value;
      const phone = codForm.querySelector('[name="customer_phone"]').value;
      const emirate = codForm.querySelector('[name="customer_emirate"]').value;
      const address = codForm.querySelector('[name="customer_address"]').value;
      const vid = codForm.querySelector('[name="cod_variant_id"]').value;

      const submitBtn = codForm.querySelector('[type="submit"]');
      const origText = submitBtn.innerHTML;
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span>⚡ Confirming COD Order...</span>';

      const orderNote = 'CASH ON DELIVERY ORDER - Name: ' + name + ' | Phone: ' + phone + ' | City: ' + emirate + ' | Address: ' + address;

      // Add to Shopify cart with note, then redirect to checkout
      const cartPayload = {
        items: [{ id: parseInt(vid, 10) || 1, quantity: 1 }],
        note: orderNote,
        attributes: {
          'Payment Method': 'Cash on Delivery (COD)',
          'Customer Phone': phone,
          'Delivery Emirate': emirate,
          'Street Address': address
        }
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
      })
      .then(() => {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: orderNote, attributes: cartPayload.attributes })
        });
      })
      .then(() => {
        closeCodModal();
        window.location.href = '/checkout';
      })
      .catch(err => {
        console.error(err);
        // Fallback direct confirmation
        alert('Thank you ' + name + '! Your Cash on Delivery order has been registered for ' + emirate + '. Our concierge will call ' + phone + ' within 30 minutes.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = origText;
        closeCodModal();
      });
    });
  }
}

// ==========================================
// 5. SHOPPING CART (DRAWER & CONTROLS)
// ==========================================
function initCartDrawer() {
  const cartTrigger = document.querySelector('[data-cart-drawer-trigger]');
  const cartBackdrop = document.querySelector('.ak-drawer-backdrop');
  const cartDrawer = document.querySelector('.ak-drawer');
  const cartClose = document.querySelector('[data-cart-drawer-close]');

  function openCart() {
    if (cartBackdrop && cartDrawer) {
      cartBackdrop.classList.add('active');
      cartDrawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      refreshCartView();
    }
  }

  function closeCart() {
    if (cartBackdrop && cartDrawer) {
      cartBackdrop.classList.remove('active');
      cartDrawer.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  if (cartTrigger) cartTrigger.addEventListener('click', openCart);
  if (cartBackdrop) cartBackdrop.addEventListener('click', closeCart);
  if (cartClose) cartClose.addEventListener('click', closeCart);

  // Quick Add
  document.addEventListener('click', function(e) {
    const qBtn = e.target.closest('[data-quick-add]');
    if (!qBtn) return;
    e.preventDefault();

    const variantId = qBtn.getAttribute('data-variant-id');
    if (!variantId) return;

    const originalHtml = qBtn.innerHTML;
    qBtn.innerHTML = '<span>Adding...</span>';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
    .then(r => r.json())
    .then(() => {
      qBtn.innerHTML = '<span>✓ Added</span>';
      setTimeout(() => { qBtn.innerHTML = originalHtml; }, 1200);
      openCart();
    })
    .catch(err => {
      console.error(err);
      qBtn.innerHTML = originalHtml;
    });
  });

  // Intercept standard form submit for smooth AJAX add
  document.addEventListener('submit', function(e) {
    const form = e.target.closest('#main-product-form');
    if (!form) return;
    e.preventDefault();

    const submitBtn = form.querySelector('[type="submit"]') || form.querySelector('button[name="add"]');
    const originalText = submitBtn ? submitBtn.innerHTML : 'Add to Bag';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span>Adding to Bag...</span>';
    }

    const formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(res => {
      if (!res.ok) throw new Error('Out of stock or unavailable.');
      return res.json();
    })
    .then(() => {
      if (submitBtn) {
        submitBtn.innerHTML = '<span>✓ Added to Bag</span>';
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
        }, 1500);
      }
      openCart();
    })
    .catch(err => {
      alert('Error: ' + err.message);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    });
  });

  // Quantity and Remove handlers
  document.addEventListener('click', function(e) {
    const plusBtn = e.target.closest('[data-cart-qty-plus]');
    if (plusBtn) {
      const lineKey = plusBtn.getAttribute('data-line-key') || plusBtn.getAttribute('data-id');
      const input = plusBtn.parentElement.querySelector('[data-cart-qty-input]');
      const currentQty = parseInt(input.value, 10) || 1;
      updateCartItem(lineKey, currentQty + 1);
      return;
    }

    const minusBtn = e.target.closest('[data-cart-qty-minus]');
    if (minusBtn) {
      const lineKey = minusBtn.getAttribute('data-line-key') || minusBtn.getAttribute('data-id');
      const input = minusBtn.parentElement.querySelector('[data-cart-qty-input]');
      const currentQty = parseInt(input.value, 10) || 1;
      updateCartItem(lineKey, Math.max(0, currentQty - 1));
      return;
    }

    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const lineKey = removeBtn.getAttribute('data-line-key') || removeBtn.getAttribute('data-id');
      updateCartItem(lineKey, 0);
      return;
    }
  });

  document.addEventListener('change', function(e) {
    const input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    const lineKey = input.getAttribute('data-line-key') || input.getAttribute('data-id');
    const newQty = Math.max(0, parseInt(input.value, 10) || 0);
    updateCartItem(lineKey, newQty);
  });
}

function updateCartItem(idOrKey, quantity) {
  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: idOrKey, quantity: quantity })
  })
  .then(res => res.json())
  .then(cart => refreshCartView(cart))
  .catch(err => console.error('Cart update error:', err));
}

function refreshCartView(cartData) {
  const render = (cart) => {
    document.querySelectorAll('.ak-cart-count').forEach(b => {
      b.textContent = cart.item_count;
    });

    const totalAed = cart.total_price / 100;
    const threshold = 150;
    const pct = Math.min(100, Math.round((totalAed / threshold) * 100));
    const remaining = Math.max(0, threshold - totalAed);

    document.querySelectorAll('.shipping-bar-fill').forEach(bar => { bar.style.width = pct + '%'; });
    document.querySelectorAll('.shipping-meter-text').forEach(text => {
      if (remaining === 0) {
        text.innerHTML = '<strong style="color:#10B981;">✓ Free UAE Express Delivery Unlocked!</strong>';
      } else {
        text.innerHTML = 'Add <strong>AED ' + remaining.toFixed(0) + '</strong> more for <strong>FREE Delivery</strong>';
      }
    });

    const drawerList = document.querySelector('[data-drawer-items]');
    const drawerSubtotal = document.querySelector('[data-drawer-subtotal]');

    if (drawerSubtotal) {
      drawerSubtotal.textContent = 'AED ' + (cart.total_price / 100).toFixed(2);
    }

    if (drawerList) {
      if (cart.items.length === 0) {
        drawerList.innerHTML = `
          <div style="text-align:center;padding:3.5rem 1rem;color:var(--color-text-muted);">
            <div style="font-size:3rem;margin-bottom:1rem;">🛍️</div>
            <div style="font-weight:800;color:var(--color-text);font-size:1.1rem;margin-bottom:0.35rem;">Your bag is empty</div>
            <p style="font-size:0.8125rem;">Discover 2,120+ authentic UAE products</p>
            <a href="/collections/all" class="btn-primary" style="margin-top:1.5rem;display:inline-block;padding:0.75rem 1.5rem;">Explore Catalog</a>
          </div>
        `;
      } else {
        drawerList.innerHTML = cart.items.map((item) => `
          <div style="display:flex;gap:1rem;padding:1rem;background:var(--color-input);border:1px solid var(--color-border);border-radius:1rem;margin-bottom:0.75rem;align-items:center;">
            <img src="${item.featured_image ? item.featured_image.url : ''}" alt="${item.title}" style="width:65px;height:65px;object-fit:contain;background:var(--color-card);border-radius:0.5rem;padding:0.25rem;">
            <div style="flex:1;min-width:0;">
              <div style="font-size:0.8125rem;font-weight:700;color:var(--color-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${item.product_title}</div>
              ${item.variant_title ? '<div style="font-size:0.6875rem;color:var(--color-text-muted);margin-top:0.1rem;">' + item.variant_title + '</div>' : ''}
              <div style="font-size:0.875rem;font-weight:900;color:var(--color-accent);margin-top:0.25rem;">
                AED ${(item.final_line_price / 100).toFixed(2)}
              </div>
              <div style="display:flex;align-items:center;justify-content:space-between;margin-top:0.5rem;">
                <div class="ak-qty-selector" style="height:2rem;">
                  <button type="button" class="ak-qty-btn" data-cart-qty-minus data-line-key="${item.key}" style="width:1.75rem;font-size:0.9rem;">-</button>
                  <input type="number" class="ak-qty-input" data-cart-qty-input data-line-key="${item.key}" value="${item.quantity}" style="width:2.2rem;font-size:0.75rem;">
                  <button type="button" class="ak-qty-btn" data-cart-qty-plus data-line-key="${item.key}" style="width:1.75rem;font-size:0.9rem;">+</button>
                </div>
                <button type="button" data-cart-remove data-line-key="${item.key}" style="background:none;border:none;color:#EF4444;font-size:0.6875rem;font-weight:700;cursor:pointer;">
                  ✕ Remove
                </button>
              </div>
            </div>
          </div>
        `).join('');
      }
    }

    const cartPageForm = document.querySelector('[data-cart-page-form]');
    if (cartPageForm) {
      if (cart.items.length === 0) {
        window.location.reload();
      } else {
        const totalEl = document.querySelector('[data-cart-page-total]');
        if (totalEl) totalEl.textContent = 'AED ' + (cart.total_price / 100).toFixed(2);

        cart.items.forEach(item => {
          const row = cartPageForm.querySelector(`[data-cart-row="${item.key}"]`);
          if (row) {
            const linePriceEl = row.querySelector('[data-line-price]');
            if (linePriceEl) linePriceEl.textContent = 'AED ' + (item.final_line_price / 100).toFixed(2);
            const input = row.querySelector('[data-cart-qty-input]');
            if (input) input.value = item.quantity;
          }
        });

        const currentKeys = new Set(cart.items.map(i => String(i.key)));
        cartPageForm.querySelectorAll('[data-cart-row]').forEach(row => {
          const k = row.getAttribute('data-cart-row');
          if (!currentKeys.has(k)) row.remove();
        });
      }
    }
  };

  if (cartData) render(cartData);
  else fetch('/cart.js').then(r => r.json()).then(render).catch(console.error);
}

// ==========================================
// 6. PRODUCT GALLERY (ZOOM, LIGHTBOX, ARROWS)
// ==========================================
function initProductGallery() {
  const mainGallery = document.querySelector('[data-gallery-main]');
  if (!mainGallery) return;

  const mainImg = mainGallery.querySelector('[data-gallery-img]');
  const thumbs = document.querySelectorAll('[data-gallery-thumb]');
  const prevBtn = mainGallery.querySelector('[data-gallery-prev]');
  const nextBtn = mainGallery.querySelector('[data-gallery-next]');
  const lightbox = document.querySelector('[data-lightbox]');
  const lightboxImg = lightbox ? lightbox.querySelector('img') : null;
  const lightboxClose = lightbox ? lightbox.querySelector('[data-lightbox-close]') : null;

  const images = [];
  thumbs.forEach(t => {
    const src = t.getAttribute('data-full-src');
    if (src) images.push(src);
  });
  if (images.length === 0 && mainImg) images.push(mainImg.src);

  let currentIndex = 0;

  function setIndex(idx) {
    if (idx < 0) idx = images.length - 1;
    if (idx >= images.length) idx = 0;
    currentIndex = idx;

    const newSrc = images[currentIndex];
    if (mainImg) mainImg.src = newSrc;
    if (lightboxImg) lightboxImg.src = newSrc;

    thumbs.forEach((t, i) => {
      if (i === currentIndex) t.classList.add('active');
      else t.classList.remove('active');
    });
  }

  thumbs.forEach((t, idx) => {
    t.addEventListener('click', () => setIndex(idx));
  });

  if (prevBtn) prevBtn.addEventListener('click', (e) => { e.stopPropagation(); setIndex(currentIndex - 1); });
  if (nextBtn) nextBtn.addEventListener('click', (e) => { e.stopPropagation(); setIndex(currentIndex + 1); });

  // Desktop Hover Zoom
  mainGallery.addEventListener('mousemove', function(e) {
    if (!mainImg) return;
    const rect = mainGallery.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    mainImg.style.transformOrigin = x + '% ' + y + '%';
    mainImg.style.transform = 'scale(2.1)';
  });

  mainGallery.addEventListener('mouseleave', function() {
    if (!mainImg) return;
    mainImg.style.transform = 'scale(1)';
    mainImg.style.transformOrigin = 'center center';
  });

  // Lightbox
  mainGallery.addEventListener('click', function(e) {
    if (e.target.closest('[data-gallery-prev]') || e.target.closest('[data-gallery-next]')) return;
    if (lightbox && lightboxImg) {
      lightboxImg.src = images[currentIndex];
      lightbox.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  });

  if (lightboxClose) {
    lightboxClose.addEventListener('click', () => {
      lightbox.classList.remove('active');
      document.body.style.overflow = '';
    });
  }
  if (lightbox) {
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) {
        lightbox.classList.remove('active');
        document.body.style.overflow = '';
      }
    });
  }

  // Swipe
  let touchStartX = 0;
  mainGallery.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
  mainGallery.addEventListener('touchend', e => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    if (diff > 40) setIndex(currentIndex + 1);
    else if (diff < -40) setIndex(currentIndex - 1);
  }, { passive: true });
}

// ==========================================
// 7. MOVING PRODUCT BANNER CAROUSEL
// ==========================================
function initMovingBanner() {
  const carousel = document.querySelector('[data-banner-carousel]');
  if (!carousel) return;

  const track = carousel.querySelector('[data-banner-track]');
  const slides = carousel.querySelectorAll('[data-banner-slide]');
  const dots = carousel.querySelectorAll('[data-banner-dot]');
  const prevBtn = carousel.querySelector('[data-banner-prev]');
  const nextBtn = carousel.querySelector('[data-banner-next]');

  if (!slides.length) return;
  let activeIndex = 0;
  let timer = null;

  function goTo(index) {
    if (index < 0) index = slides.length - 1;
    if (index >= slides.length) index = 0;
    activeIndex = index;
    if (track) track.style.transform = 'translateX(-' + (activeIndex * 100) + '%)';
    dots.forEach((dot, i) => {
      if (i === activeIndex) dot.classList.add('active');
      else dot.classList.remove('active');
    });
  }

  function startAutoplay() {
    stopAutoplay();
    timer = setInterval(() => goTo(activeIndex + 1), 3800);
  }

  function stopAutoplay() {
    if (timer) clearInterval(timer);
  }

  carousel.addEventListener('mouseenter', stopAutoplay);
  carousel.addEventListener('mouseleave', startAutoplay);

  if (prevBtn) prevBtn.addEventListener('click', () => { goTo(activeIndex - 1); startAutoplay(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { goTo(activeIndex + 1); startAutoplay(); });

  dots.forEach((dot, idx) => {
    dot.addEventListener('click', () => { goTo(idx); startAutoplay(); });
  });

  startAutoplay();
}

// ==========================================
// 8. QUANTITY CONTROLS (PRODUCT PAGE)
// ==========================================
function initQuantityControls() {
  document.addEventListener('click', function(e) {
    const plus = e.target.closest('[data-prod-qty-plus]');
    if (plus) {
      const input = plus.parentElement.querySelector('[data-product-qty]');
      if (input) input.value = (parseInt(input.value, 10) || 1) + 1;
      return;
    }
    const minus = e.target.closest('[data-prod-qty-minus]');
    if (minus) {
      const input = minus.parentElement.querySelector('[data-product-qty]');
      if (input) input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      return;
    }
  });
}

// ==========================================
// 9. CUSTOMER REVIEWS FORM
// ==========================================
function initReviewForm() {
  const form = document.querySelector('[data-review-form]');
  if (!form) return;

  const starInputs = form.querySelectorAll('input[name="rating"]');
  const reviewList = document.querySelector('[data-review-list]');

  form.addEventListener('submit', function(e) {
    e.preventDefault();
    const name = form.querySelector('[name="author"]').value || 'UAE Shopper';
    const city = form.querySelector('[name="city"]').value || 'Dubai';
    const comment = form.querySelector('[name="body"]').value;
    let rating = 5;
    starInputs.forEach(r => { if (r.checked) rating = r.value; });

    if (!comment) return;
    const starsHtml = '★'.repeat(rating) + '☆'.repeat(5 - rating);

    const newReview = document.createElement('div');
    newReview.style.cssText = 'background:var(--color-input);border:1px solid var(--color-border);padding:1.25rem;border-radius:1rem;margin-bottom:1rem;';
    newReview.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.5rem;">
        <div>
          <span style="font-weight:800;color:var(--color-text);font-size:0.875rem;">${name}</span>
          <span style="font-size:0.6875rem;color:var(--color-accent);margin-left:0.5rem;font-weight:700;">✓ Verified UAE Buyer (${city})</span>
        </div>
        <span style="color:#F59E0B;letter-spacing:0.1em;font-size:0.875rem;">${starsHtml}</span>
      </div>
      <p style="color:var(--color-text-muted);font-size:0.8125rem;line-height:1.6;">${comment}</p>
      <div style="font-size:0.6875rem;color:var(--color-text-muted);margin-top:0.5rem;">Just now • Verified Purchase</div>
    `;

    if (reviewList) reviewList.prepend(newReview);
    form.reset();
    alert('Thank you! Your verified review has been published.');
  });
}
